/**
 * The one place the PWA talks to the API.
 *
 * Three things every call needs and none should have to remember:
 *  - credentials: 'include' — auth is a cookie, not a bearer token (08 §1).
 *  - the CSRF header on state-changing calls (08 §3). The token comes from /auth/me and lives
 *    in memory only; putting it in localStorage would hand it to any XSS that got through.
 *  - RFC 7807 problem responses turned into a typed error, so a component can show
 *    `problem.title` without parsing anything.
 */

import { setAgentCsrf } from './agent'

export interface Problem {
  type: string
  title: string
  status: number
  detail?: string
  instance?: string
  request_id?: string
  errors?: { field: string; message: string }[]
  [key: string]: unknown
}

export class ApiError extends Error {
  readonly problem: Problem
  readonly status: number

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title)
    this.name = 'ApiError'
    this.problem = problem
    this.status = problem.status
  }

  /** Field -> message, for inline form validation (01 §3.2). */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const error of this.problem.errors ?? []) out[error.field] = error.message
    return out
  }
}

const BASE = '/api/v1'
const SAFE = new Set(['GET', 'HEAD', 'OPTIONS'])

let csrfToken: string | null = null

export function setCsrfToken(token: string | null): void {
  csrfToken = token
  // The agent turn is a streaming POST made with bare `fetch`, so it cannot go through
  // `request()` — it still needs the same token, from the same place, at the same moment.
  setAgentCsrf(token)
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Collapses a retried submit into one server-side action (05 §5 "Idempotency-Key"). */
  idempotencyKey?: string
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase()
  const headers = new Headers(options.headers)
  headers.set('Accept', 'application/json')

  if (options.body !== undefined) headers.set('Content-Type', 'application/json')
  if (!SAFE.has(method) && csrfToken) headers.set('X-CSRF-Token', csrfToken)
  if (options.idempotencyKey) headers.set('Idempotency-Key', options.idempotencyKey)

  const response = await fetch(`${BASE}${path}`, {
    ...options,
    method,
    headers,
    credentials: 'include',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  })

  if (response.status === 204) return undefined as T

  const isProblem = response.headers.get('content-type')?.includes('problem+json')
  if (!response.ok) {
    if (isProblem) throw new ApiError((await response.json()) as Problem)
    throw new ApiError({
      type: 'about:blank',
      title: 'Something went wrong',
      status: response.status,
      request_id: response.headers.get('x-request-id') ?? undefined,
    })
  }

  return (await response.json()) as T
}

/**
 * A multipart POST, for the one thing the JSON helper cannot carry: a file.
 *
 * Deliberately not routed through `request()`. That helper sets `Content-Type: application/json`
 * and stringifies the body, and a `FormData` needs neither — the browser has to set the header
 * itself so it can include the multipart boundary. Setting it by hand produces a request the
 * server cannot parse and an error that says nothing about why.
 */
async function upload<T>(path: string, form: FormData): Promise<T> {
  const headers = new Headers({ Accept: 'application/json' })
  if (csrfToken) headers.set('X-CSRF-Token', csrfToken)

  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: form,
  })

  if (!response.ok) {
    const isProblem = response.headers.get('content-type')?.includes('problem+json')
    if (isProblem) throw new ApiError((await response.json()) as Problem)
    throw new ApiError({
      type: 'about:blank',
      title: 'Something went wrong',
      status: response.status,
      request_id: response.headers.get('x-request-id') ?? undefined,
    })
  }
  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  upload,
  post: <T>(path: string, body?: unknown, idempotencyKey?: string) =>
    request<T>(path, { method: 'POST', body, idempotencyKey }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

export interface Identity {
  principal: 'homeowner' | 'staff' | 'anonymous'
  id: string | null
  role: string | null
  csrf_token: string | null
  must_change_password: boolean
}

export async function fetchIdentity(): Promise<Identity> {
  const identity = await api.get<Identity>('/auth/me')
  setCsrfToken(identity.csrf_token)
  return identity
}
